<template>
  <RevCard class="p-24">
    <div class="mb-24 flex items-center justify-between">
      <div class="heading-2 flex items-center gap-8">
        <IconShoppingBag />
        {{ i18n(translations.saleInsights) }}
      </div>

      <div class="flex items-center gap-24">
        {{ i18n(translations.lastSevenDays) }}

        <RevButton
          size="small"
          :to="{ name: ROUTE_NAMES.INSIGHTS.INDEX }"
          variant="secondary"
        >
          {{ i18n(translations.viewDetails) }}
        </RevButton>
      </div>
    </div>

    <div>
      <RevLoadingScreen
        v-if="isLoading"
        class="mt-32"
        :text="i18n(translations.loading)"
      />

      <div v-else class="grid grid-cols-3 gap-24">
        <div
          class="border-static-default-mid rounded-sm flex w-full flex-col justify-between gap-12 border p-24"
        >
          <p class="body-1 flex items-start gap-8">
            <IconShoppingBag />
            {{ i18n(translations.salesMetricsOrderBlockTitle) }}
          </p>

          <div>
            <span
              v-if="salesMetrics && salesMetrics.receivedOrdersCount > 0"
              class="heading-2 mt-2"
            >
              {{ salesMetrics.receivedOrdersCount }}
            </span>
            <span v-else class="heading-2 mt-2">
              {{ i18n(translations.noOrders) }}
            </span>
          </div>
        </div>

        <div
          class="border-static-default-mid rounded-sm flex w-full flex-col justify-between gap-12 border p-24"
        >
          <p class="body-1 flex items-start gap-8">
            <IconMoney />
            {{ i18n(translations.salesMetricsSalesRevenueBlockTitle) }}
          </p>

          <div v-if="salesMetrics?.salesRevenue.length">
            <div
              v-for="(revenue, index) in salesMetrics.salesRevenue"
              :key="index + '-' + revenue"
              class="heading-2"
            >
              {{ i18n.price(revenue) }}
            </div>
          </div>
          <div v-else class="heading-2">
            {{ i18n(translations.noSalesRevenue) }}
          </div>
        </div>

        <div
          class="border-static-default-mid rounded-sm flex w-full flex-col justify-between gap-12 border p-24"
        >
          <p class="body-1 flex items-start gap-8">
            <IconRefund />
            {{ i18n(translations.salesMetricsTotalRefundBlockTitle) }}
          </p>

          <div v-if="salesMetrics?.refundedAmount.length">
            <div
              v-for="(refundedAmount, index) in salesMetrics.refundedAmount"
              :key="index"
              class="heading-2"
            >
              {{ i18n.price(refundedAmount) }}
            </div>
          </div>
          <div v-else class="heading-2">
            {{ i18n(translations.noRefunds) }}
          </div>
        </div>
      </div>
    </div>
  </RevCard>
</template>

<script setup lang="ts">
import { type Error, getApiErrorMessage } from '#imports'

import { merchantsAPI } from '@backmarket/http-api'
import { useHttpFetch } from '@backmarket/nuxt-module-http/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevButton } from '@ds/components/Button'
import { RevCard } from '@ds/components/Card'
import { RevLoadingScreen } from '@ds/components/LoadingScreen'
import { IconMoney } from '@ds/icons/IconMoney'
import { IconRefund } from '@ds/icons/IconRefund'
import { IconShoppingBag } from '@ds/icons/IconShoppingBag'

import { ROUTE_NAMES } from '~/constants/routes'

import translations from './SaleInsights.translations'

const i18n = useI18n()
const { openErrorToast } = useTheToast()

const {
  data: salesMetrics,
  error,
  pending: isLoading,
} = await useHttpFetch(merchantsAPI.getHomepageSalesMetrics)

if (error.value) {
  openErrorToast({
    content: getApiErrorMessage(error.value as unknown as Error),
  })
}
</script>
